/**
 * Whop checkout-plan picker.
 *
 * Two modes:
 *  1) Legacy 2-way A/B: pickCheckoutPlan({ userId, controlPlanId, variantPlanId, ratio })
 *     - Deterministic per userId (sticky), bucketed via `userId % 100` against `ratio * 100`.
 *     - Anonymous (no userId) falls back to Math.random().
 *     - variant ∈ { 'control', 'variant' }
 *
 *  2) New 3-way A/B/C 33/33/33: pickCheckoutPlanThreeWay({ userId, planA, planB, planC })
 *     - Deterministic per userId via `userId % 3`  (0 → A, 1 → B, 2 → C).
 *     - Anonymous fallback via Math.random() into 3 equal buckets.
 *     - splitVariant ∈ { 'A', 'B', 'C' }
 *     - For backward-compat with existing call sites that switch on
 *       `result.variant === 'variant'`, A is mapped to 'control', and B + C are
 *       mapped to 'variant' on the legacy `variant` field.
 *
 * The legacy 2-way function is still exported because email/copy code paths
 * read `result.variant` to gate per-variant strings; renaming would silently
 * break them. New callers should prefer pickCheckoutPlanThreeWay() and read
 * the `splitVariant` field.
 */

export interface PickCheckoutPlanInput {
  userId?: number | string | null;
  controlPlanId: string;
  variantPlanId?: string | null;
  ratio: number;
}

export interface PickCheckoutPlanResult {
  planId: string;
  baseUrl: string;
  /** Legacy two-way label. A → 'control', B|C → 'variant'. */
  variant: 'control' | 'variant';
  /** Three-way label when produced by pickCheckoutPlanThreeWay; absent in legacy 2-way results. */
  splitVariant?: 'A' | 'B' | 'C';
}

function buildResult(planId: string, variant: 'control' | 'variant', splitVariant?: 'A' | 'B' | 'C'): PickCheckoutPlanResult {
  return {
    planId,
    baseUrl: `https://whop.com/checkout/${planId}`,
    variant,
    ...(splitVariant ? { splitVariant } : {}),
  };
}

export function pickCheckoutPlan(input: PickCheckoutPlanInput): PickCheckoutPlanResult {
  const { userId, controlPlanId, variantPlanId, ratio } = input;

  if (!variantPlanId || ratio <= 0) {
    return buildResult(controlPlanId, 'control');
  }

  if (userId !== null && userId !== undefined && userId !== '') {
    const num = Number(userId);
    if (Number.isFinite(num)) {
      const bucket = Math.abs(Math.trunc(num)) % 100;
      const threshold = Math.round(ratio * 100);
      return bucket < threshold
        ? buildResult(variantPlanId, 'variant')
        : buildResult(controlPlanId, 'control');
    }
  }

  return Math.random() < ratio
    ? buildResult(variantPlanId, 'variant')
    : buildResult(controlPlanId, 'control');
}

export interface PickCheckoutPlanThreeWayInput {
  userId?: number | string | null;
  planA: string;
  planB: string;
  planC: string;
}

/**
 * Deterministic 3-way 33/33/33 split.
 *
 * For numeric userIds (the common case once a user is logged in), this is
 * sticky: the same userId always lands in the same bucket. For anonymous
 * (pre-login) callers, it falls back to a single uniform Math.random() draw.
 *
 * If you need cross-session stickiness for anonymous users, store the
 * resulting `splitVariant` in localStorage at the call site and pass a
 * synthesised string userId on subsequent calls.
 */
export function pickCheckoutPlanThreeWay(input: PickCheckoutPlanThreeWayInput): PickCheckoutPlanResult {
  const { userId, planA, planB, planC } = input;

  // Defensive: if any plan is missing, degrade to A only.
  if (!planA || !planB || !planC) {
    return buildResult(planA || planB || planC, 'control', 'A');
  }

  if (userId !== null && userId !== undefined && userId !== '') {
    const num = Number(userId);
    if (Number.isFinite(num)) {
      const bucket = Math.abs(Math.trunc(num)) % 3;
      if (bucket === 0) return buildResult(planA, 'control', 'A');
      if (bucket === 1) return buildResult(planB, 'variant', 'B');
      return buildResult(planC, 'variant', 'C');
    }
  }

  // Anonymous: uniform random across 3 buckets.
  const r = Math.random();
  if (r < 1 / 3) return buildResult(planA, 'control', 'A');
  if (r < 2 / 3) return buildResult(planB, 'variant', 'B');
  return buildResult(planC, 'variant', 'C');
}
